/**
 * Déclaration des tâches image — chaque tâche liste ses champs ; les champs
 * communs (prompt, rendu, seed…) sont partagés. AJOUTER UN SOUS-MODE IMAGE :
 * définir une entrée ici, le moteur (TaskForm) fait le reste.
 */
import type { ImageTaskDef, TaskFieldDef } from "./TaskForm.js";

const needsSource = (s: { taskId: string }) =>
	s.taskId === "img2img" || s.taskId === "inpaint" || s.taskId === "edit";

const commonFields: TaskFieldDef[] = [
	{
		id: "prompt",
		label: "Décrivez l'image",
		kind: "textarea",
		defaultValue: "",
		placeholder: "Par exemple : une cabane en bois dans une forêt enneigée, au crépuscule",
		max: 10000,
	},
	{
		id: "negative_prompt",
		label: "À éviter (facultatif)",
		kind: "textarea",
		defaultValue: "",
		placeholder: "Par exemple : flou, texte, mains déformées",
		advanced: true,
	},
	{
		id: "enhance_prompt",
		label: "Enrichir ma description automatiquement",
		kind: "checkbox",
		defaultValue: false,
	},
	{
		id: "width",
		label: "Largeur",
		advanced: true,
		kind: "slider",
		defaultValue: 1024,
		min: 256,
		max: 5120,
		step: 8,
		format: (v) => `${v}px`,
	},
	{
		id: "height",
		label: "Hauteur",
		advanced: true,
		kind: "slider",
		defaultValue: 1024,
		min: 256,
		max: 5120,
		step: 8,
		format: (v) => `${v}px`,
	},
	{
		id: "scheduler",
		label: "Méthode de calcul (sampler)",
		advanced: true,
		kind: "select",
		defaultValue: "euler_a",
		options: [{ value: "euler_a", label: "Euler Ancestral" }],
		// Un modèle distillé (LCM) ne débruite qu'avec sa propre méthode : la
		// machine la garde quoi qu'on choisisse ici, alors on le dit.
		state: ({ form }) => {
			const own = form.model?.own_scheduler;
			return own
				? {
						disabled: true,
						note: `Ce modèle calcule avec sa propre méthode (${own}) : le réglage ne changerait rien.`,
					}
				: {};
		},
	},
	{
		id: "steps",
		label: "Étapes (plus = plus fin, plus long)",
		kind: "slider",
		defaultValue: 30,
		min: 1,
		max: 100,
		advanced: true,
	},
	{
		id: "guidance",
		label: "Fidélité à la description (guidage)",
		advanced: true,
		kind: "slider",
		defaultValue: 7.5,
		min: 0,
		max: 20,
		step: 0.5,
		/**
		 * Deux zones de ce curseur ne font rien, prouvées à l'octet près le
		 * 30/09/2026, et la page les proposait à tout le monde.
		 *
		 * Sous 1 (inclus), diffusers coupe purement le guidage : 0,0 et 1,0
		 * rendent la même image sur deux modèles distillés indépendants, 2,0
		 * une autre. Et FLUX.1-schnell l'ignore à toute valeur, son
		 * transformeur étant distillé sans l'encastrement qui la porte.
		 *
		 * Le serveur sert les deux faits (`guidance_supported`,
		 * `guidance_floor`) ; on ne les redéduit pas ici du nom du modèle.
		 */
		state: ({ form, value }) => {
			const model = form.model;
			if (model && model.guidance_supported === false) {
				return {
					disabled: true,
					note: "Ce modèle ignore le guidage : il a été distillé sans. Le réglage ne changerait rien.",
				};
			}
			const floor = model?.guidance_floor ?? 1;
			if (floor > 0 && typeof value === "number" && value <= floor) {
				return {
					note: `À ${floor.toFixed(1).replace(".", ",")} ou moins, le guidage est désactivé : toutes ces valeurs donnent la même image.`,
				};
			}
			return {};
		},
	},
	{
		id: "seed",
		label: "Graine (la même graine refait la même image)",
		kind: "seed",
		defaultValue: -1,
		advanced: true,
	},
	{
		id: "batch_size",
		label: "Nombre d'images",
		kind: "number",
		defaultValue: 1,
		min: 1,
		max: 4,
		advanced: true,
	},
];

const sourceField: TaskFieldDef = {
	id: "init_image",
	label: "Image de départ",
	kind: "source-image",
	defaultValue: null,
	visible: needsSource,
	help: "Image de départ : glissez-déposez ou parcourez. PNG, JPEG, WebP.",
};

export const imageTasks: ImageTaskDef[] = [
	{
		id: "txt2img",
		label: "Créer",
		mode: "txt2img",
		fields: [...commonFields],
	},
	{
		id: "img2img",
		label: "Transformer une image",
		mode: "img2img",
		fields: [
			sourceField,
			{
				id: "strength",
				label: "Force de transformation",
				kind: "slider",
				defaultValue: 0.75,
				min: 0.01,
				max: 1,
				step: 0.01,
				format: (v) => v.toFixed(2),
			},
			...commonFields,
		],
	},
	{
		id: "inpaint",
		label: "Retoucher une zone",
		mode: "inpaint",
		fields: [
			sourceField,
			{
				id: "mask_image",
				label: "Masque (blanc = zone à repeindre)",
				kind: "mask",
				defaultValue: null,
				visible: (s) => s.taskId === "inpaint",
				help: "Masque noir/blanc aux mêmes dimensions que l'image source.",
			},
			...commonFields,
		],
	},
	{
		id: "controlnet",
		label: "Suivre une forme",
		mode: "controlnet",
		fields: [
			{
				id: "controlnet_type",
				label: "Forme à suivre",
				kind: "select",
				defaultValue: "canny",
				options: [
					{ value: "canny", label: "Canny (contours)" },
					{ value: "depth", label: "Profondeur" },
					{ value: "pose", label: "Pose" },
					{ value: "scribble", label: "Scribble (croquis)" },
					{ value: "lineart", label: "Lineart (traits fins)" },
				],
			},
			{
				id: "control_image",
				label: "Image de référence",
				kind: "control-upload",
				defaultValue: null,
				help: "Votre ordinateur en tire les contours, la profondeur ou la pose, puis l'image suit cette forme. Modèles SD 1.5 et SDXL.",
			},
			{
				id: "controlnet_scale",
				label: "Intensité du contrôle",
				kind: "slider",
				defaultValue: 1.0,
				min: 0,
				max: 2,
				step: 0.05,
				format: (v) => v.toFixed(2),
			},
			...commonFields,
		],
	},
	{
		id: "edit",
		label: "Modifier par instruction",
		// Envoyé comme un img2img — c'est le même appel — mais offert selon sa
		// propre capacité : déclarée avec `mode: "img2img"`, la pastille
		// apparaissait sur tout modèle sachant faire de l'image → image, donc
		// « Édition (FLUX Kontext) » sur un SD 1.5.
		mode: "img2img",
		requires: "edit",
		fields: [
			{
				...sourceField,
				label: "Image à éditer",
				help: "Décrivez la modification souhaitée (« ajoute un chapeau », « mets en hiver »…). Demande le modèle FLUX.1 Kontext.",
			},
			...commonFields,
		],
	},
];
